/**
 * <s-promo-popup> — shows a discount code popup once in a while.
 *
 * Opens the inner <dialog> after data-delay seconds on the page, then stays quiet for data-snooze-days
 * (remembered in localStorage). Closing is native (command="close"); backdrop clicks come from <s-dialog>.
 *
 * Markup:
 *   dialog — the popup
 *   [data-promo-code] — element whose text is the code to copy
 *   [data-promo-countdown] — shows time left until local midnight while open
 *   button[command="--copy-code"][commandfor=this id] — copies the code; label swaps to data-copied-label
 *   button[command="--apply-discount"] — adds data-code to the cart (label swaps to data-applied-label)
 *
 * Same-day only: the day the code is added is remembered, and on any later day the code is taken off the cart again
 * (other discount codes in the cart are kept). Needs data-cart-url and data-cart-update-url from Liquid routes.
 */
const DEFAULT_STORAGE_KEY = 'vl-promo-popup-seen';
const APPLIED_KEY = 'vl-promo-applied';

class SPromoPopup extends HTMLElement {
  #ac;
  #timer;
  #resetTimer;
  #countdown;

  connectedCallback() {
    this.#ac = new AbortController();
    this.addEventListener('command', (event) => {
      if (event.command === '--copy-code') this.#copy(event.source);
      if (event.command === '--apply-discount') this.#apply(event.source);
    }, { signal: this.#ac.signal });

    this.#expireOldDiscount();
    if (window.Shopify?.designMode || !this.#isDue()) return;
    const delay = Number(this.dataset.delay || 12) * 1000;
    this.#timer = setTimeout(() => this.#open(), delay);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    clearTimeout(this.#timer);
    clearInterval(this.#countdown);
    clearTimeout(this.#resetTimer);
  }

  // Keyed by promo code (data-storage-key), so a new code shows again to everyone.
  get #storageKey() {
    return this.dataset.storageKey || DEFAULT_STORAGE_KEY;
  }

  #isDue() {
    try {
      const seen = Number(localStorage.getItem(this.#storageKey) || 0);
      const snooze = Number(this.dataset.snoozeDays || 7) * 86400000;
      return Date.now() - seen > snooze;
    } catch {
      return true;
    }
  }

  #open() {
    const dialog = this.querySelector('dialog');
    // Never interrupt another open dialog, like the cart drawer or menu.
    if (!dialog || document.querySelector('dialog[open]')) return;
    try {
      localStorage.setItem(this.#storageKey, String(Date.now()));
    } catch {
      // Storage unavailable: the popup simply may show again next visit.
    }
    this.#startCountdown(dialog);
    dialog.showModal();
  }

  // Counts down to the visitor's local midnight, the last moment this popup can be seen.
  #startCountdown(dialog) {
    const output = this.querySelector('[data-promo-countdown]');
    if (!output) return;
    const tick = () => {
      const now = new Date();
      const midnight = new Date(now);
      midnight.setHours(24, 0, 0, 0);
      const left = Math.max(0, Math.floor((midnight - now) / 1000));
      const pad = (n) => String(n).padStart(2, '0');
      output.textContent = `${pad(Math.floor(left / 3600))}:${pad(Math.floor((left % 3600) / 60))}:${pad(left % 60)}`;
    };
    tick();
    clearInterval(this.#countdown);
    this.#countdown = setInterval(tick, 1000);
    dialog.addEventListener('close', () => clearInterval(this.#countdown), { once: true, signal: this.#ac.signal });
  }



  #today() {
    const now = new Date();
    return `${now.getFullYear()}-${now.getMonth() + 1}-${now.getDate()}`;
  }

  async #cartCodes() {
    const response = await fetch(`${this.dataset.cartUrl}.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('cart');
    const cart = await response.json();
    return (cart.discount_codes || []).map((discount) => discount.code);
  }

  async #setCartCodes(codes) {
    const response = await fetch(`${this.dataset.cartUpdateUrl}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: codes.join(',') }),
    });
    if (!response.ok) throw new Error('cart update');
  }

  async #apply(button) {
    const code = this.dataset.code;
    if (!code) return;
    try {
      const codes = await this.#cartCodes();
      const others = codes.filter((c) => c.toLowerCase() !== code.toLowerCase());
      await this.#setCartCodes([...others, code]);
      localStorage.setItem(APPLIED_KEY, JSON.stringify({ code, day: this.#today() }));
    } catch {
      return;
    }
    if (button instanceof HTMLElement && button.dataset.appliedLabel) button.textContent = button.dataset.appliedLabel;
    setTimeout(() => this.querySelector('dialog')?.close(), 1200);
  }

  // After the day it was added, take the popup code off the cart so it only counts on that day.
  async #expireOldDiscount() {
    let applied;
    try {
      applied = JSON.parse(localStorage.getItem(APPLIED_KEY) || 'null');
    } catch {
      return;
    }
    if (!applied?.code || applied.day === this.#today()) return;
    try {
      const codes = await this.#cartCodes();
      const others = codes.filter((c) => c.toLowerCase() !== applied.code.toLowerCase());
      if (others.length !== codes.length) await this.#setCartCodes(others);
      localStorage.removeItem(APPLIED_KEY);
    } catch {
      // Try again on the next page view.
    }
  }

  async #copy(button) {
    const code = this.querySelector('[data-promo-code]')?.textContent.trim();
    if (!code || !(button instanceof HTMLElement)) return;
    try {
      await navigator.clipboard.writeText(code);
    } catch {
      return;
    }
    const label = button.dataset.label || button.textContent;
    button.dataset.label = label;
    button.textContent = button.dataset.copiedLabel || label;
    clearTimeout(this.#resetTimer);
    this.#resetTimer = setTimeout(() => { button.textContent = label; }, 2000);
  }
}

if (!customElements.get('s-promo-popup')) {
  customElements.define('s-promo-popup', SPromoPopup);
}
